import type { FC, HTMLInputTypeAttribute, ReactNode } from 'react'
import type {
  DefaultValues,
  FieldPath,
  FieldValues,
  UseFormReturn
} from 'react-hook-form'
import type { z } from 'zod'

export enum FieldType {
  INPUT = 'INPUT',
  SELECT = 'SELECT',
  MULTI_SELECT = 'MULTI_SELECT',
  CHECKBOX = 'CHECKBOX',
  RADIO = 'RADIO',
  DATE = 'DATE',
  DATE_TIME = 'DATE_TIME',
  TEXTAREA = 'TEXTAREA',
  FILE = 'FILE',
  CUSTOM = 'CUSTOM'
}

export interface IInputFieldProps<
  T extends FieldValues,
  N extends FieldPath<T>
> {
  name: N
  label?: string
  type?: FieldType.INPUT
  htmlType?: HTMLInputTypeAttribute
  placeholder?: string
  step?: string
  icon?: ReactNode
  format?: string
  mask?: string
}

export interface ISelectFieldProps<
  T extends FieldValues,
  N extends FieldPath<T>
> {
  name: N
  label?: string
  type: FieldType.SELECT
  options: { value: string; label: string }[]
  placeholder?: string
  onChange?: (value: string, methods: UseFormReturn<T>) => void
}

export interface IMultiSelectFieldProps<
  T extends FieldValues,
  N extends FieldPath<T>
> {
  name: N
  label?: string
  type: FieldType.MULTI_SELECT
  options: { value: string; label: string }[]
  placeholder?: string
}

export interface ICheckboxFieldProps<
  T extends FieldValues,
  N extends FieldPath<T>
> {
  name: N
  label?: string
  checkboxLabel?: string
  type: FieldType.CHECKBOX
  onChange?: (checked: boolean, methods: UseFormReturn<T>) => void
}

export interface IRadioFieldProps<
  T extends FieldValues,
  N extends FieldPath<T>
> {
  name: N
  label?: string
  type: FieldType.RADIO
  options: { value: string | boolean; label: string }[]
}

export interface IDateFieldProps<
  T extends FieldValues,
  N extends FieldPath<T>
> {
  name: N
  label?: string
  type: FieldType.DATE
  placeholder?: string
}

export interface IDateTimeFieldProps<
  T extends FieldValues,
  N extends FieldPath<T>
> {
  name: N
  label?: string
  type: FieldType.DATE_TIME
  placeholder?: string
}

export interface ITextareaFieldProps<
  T extends FieldValues,
  N extends FieldPath<T>
> {
  name: N
  label?: string
  type: FieldType.TEXTAREA
  placeholder?: string
}

export interface IFileFieldProps<
  T extends FieldValues,
  N extends FieldPath<T>
> {
  name: N
  label?: string
  type: FieldType.FILE
  multiple?: boolean
  maxFiles?: number
  maxSize?: number
}

export type ICustomFieldComponentProps<
  T extends FieldValues,
  N extends FieldPath<T>,
  P = Record<string, unknown>
> = {
  name: N
  label?: string
  placeholder?: string
  disabled?: boolean
  readonly?: boolean
} & P

export interface ICustomFieldProps<
  T extends FieldValues,
  N extends FieldPath<T> = FieldPath<T>,
  P = Record<string, unknown>
> {
  name: N
  label?: string
  placeholder?: string
  type: FieldType.CUSTOM
  CustomComponent: FC<ICustomFieldComponentProps<T, N, P>>
  customComponentProps?: P
}

export type IFieldProps<T extends FieldValues, N extends FieldPath<T>> =
  | IInputFieldProps<T, N>
  | ISelectFieldProps<T, N>
  | IMultiSelectFieldProps<T, N>
  | ICheckboxFieldProps<T, N>
  | IRadioFieldProps<T, N>
  | IDateFieldProps<T, N>
  | IDateTimeFieldProps<T, N>
  | ITextareaFieldProps<T, N>
  | IFileFieldProps<T, N>
  | ICustomFieldProps<T, N>

export type IFormField<T extends FieldValues> = {
  [N in FieldPath<T>]: {
    disabled?: boolean
    isLoading?: boolean
    className?: string
    tooltip?: string
    ignoreField?:
      | boolean
      | ((methods: UseFormReturn<T>, field: IFormField<T>) => boolean)
  } & IFieldProps<T, N>
}[FieldPath<T>]

export interface IFormConfig<T extends FieldValues> {
  fields: IFormField<T>[]
  schema: z.ZodType<T, z.ZodTypeDef, any>
  defaultValues?: DefaultValues<T>
}
